import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Car, Pencil, Save, Trash2, Upload, Plus, ClipboardList, ChevronRight, Camera, UserCog as UserSwap,
} from "lucide-react";
import { STATUS_LABELS } from "@/components/EditarJobDialog";
import EditarVeiculoClienteDialog from "@/components/EditarVeiculoClienteDialog";
import TransferirClienteDialog from "@/components/TransferirClienteDialog";

const PRIORITY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "importante", label: "Importante" },
  { value: "urgente", label: "Urgente" },
  { value: "monitorar", label: "Monitorar" },
];

// ---------- Vehicle Edit Dialog ----------
function EditarVeiculoDialog({ vehicle, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && vehicle) {
      setForm({
        client_id: vehicle.client_id,
        vin: vehicle.vin || "",
        plate: vehicle.plate || "",
        year: vehicle.year || "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        trim: vehicle.trim || "",
        engine: vehicle.engine || "",
        fuel: vehicle.fuel || "Gasolina",
        drivetrain: vehicle.drivetrain || "",
        mileage: vehicle.mileage || "",
        photo_url: vehicle.photo_url || "",
      });
    }
  }, [open, vehicle]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model) return toast.error("Marca e modelo são obrigatórios");
    setSaving(true);
    try {
      await api.put(`/vehicles/${vehicle.id}`, {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
      });
      toast.success("Veículo atualizado");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Foto enviada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>Editar Veículo</DialogTitle>
          <DialogDescription>Ajuste os dados do veículo. Alterações são refletidas em todos os módulos.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Ano</Label>
              <Input type="number" value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="edit-vehicle-year" />
            </div>
            <div>
              <Label>Marca *</Label>
              <Input value={form.make || ""} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="edit-vehicle-make" />
            </div>
            <div>
              <Label>Modelo *</Label>
              <Input value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="edit-vehicle-model" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>VIN</Label>
              <Input value={form.vin || ""} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} maxLength={17} className="font-mono" data-testid="edit-vehicle-vin" />
            </div>
            <div>
              <Label>Placa</Label>
              <Input value={form.plate || ""} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} data-testid="edit-vehicle-plate" />
            </div>
            <div>
              <Label>Milhagem (Mileage)</Label>
              <Input type="number" value={form.mileage || ""} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="edit-vehicle-mileage" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Versão</Label>
              <Input value={form.trim || ""} onChange={(e) => setForm({ ...form, trim: e.target.value })} />
            </div>
            <div>
              <Label>Motor</Label>
              <Input value={form.engine || ""} onChange={(e) => setForm({ ...form, engine: e.target.value })} />
            </div>
            <div>
              <Label>Combustível</Label>
              <Select value={form.fuel || "Gasolina"} onValueChange={(v) => setForm({ ...form, fuel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Gasolina", "Diesel", "Flex", "Elétrico", "Híbrido"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Foto</Label>
            <div className="flex gap-2">
              <Input value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" />
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm shrink-0">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
            </div>
            {form.photo_url && <img src={form.photo_url} alt="preview" className="mt-2 h-24 rounded border border-border object-cover" />}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="edit-vehicle-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main Page ----------
export default function VeiculoDetalhe() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [editJobId, setEditJobId] = useState(null);
  const [confirmDelVehicle, setConfirmDelVehicle] = useState(false);
  const [confirmDelJob, setConfirmDelJob] = useState(null);
  const [uploadingCardPhoto, setUploadingCardPhoto] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/vehicles/${id}`);
      setData(data);
    } catch {
      toast.error("Veículo não encontrado");
      navigate("/veiculos");
    }
  };

  useEffect(() => { load(); }, [id]);

  const onQuickPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !data?.vehicle) return;
    setUploadingCardPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${up.url}${token ? `?auth=${token}` : ""}`;
      const v = data.vehicle;
      await api.put(`/vehicles/${v.id}`, {
        client_id: v.client_id, year: v.year, make: v.make, model: v.model,
        vin: v.vin, plate: v.plate, mileage: v.mileage, engine: v.engine, fuel: v.fuel,
        trim: v.trim, drivetrain: v.drivetrain, photo_url: url,
      });
      toast.success("Foto do veículo atualizada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar foto");
    } finally {
      setUploadingCardPhoto(false);
      e.target.value = "";
    }
  };

  const deleteVehicle = async () => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Veículo removido");
      navigate("/veiculos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  const deleteJob = async (jobId) => {
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success("OS removida");
      setConfirmDelJob(null);
      load();
    } catch {
      toast.error("Erro ao remover OS");
    }
  };

  const priorityBadge = (p) => {
    const map = {
      urgente: "bg-red-500/15 text-red-500 border-red-500/20",
      importante: "bg-orange-500/15 text-orange-500 border-orange-500/20",
      monitorar: "bg-secondary text-foreground border-border",
    };
    return map[p] || "bg-secondary text-secondary-foreground border-border";
  };

  const stats = useMemo(() => {
    if (!data) return { total: 0, ativos: 0 };
    const total = (data.jobs || []).length;
    const ativos = (data.jobs || []).filter((j) => !["entregue", "aguardando_pagamento"].includes(j.status)).length;
    return { total, ativos };
  }, [data]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Carregando…</div>;
  const { vehicle, client, jobs } = data;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="veiculo-detalhe-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Button>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="h-48 md:h-56 bg-secondary relative group">
          {vehicle.photo_url ? (
            <img src={vehicle.photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Car className="w-12 h-12 text-muted-foreground" /></div>
          )}
          <label
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/70 hover:bg-black text-white text-xs font-medium cursor-pointer transition backdrop-blur-sm"
            data-testid="quick-change-photo-btn"
          >
            <Camera className="w-3.5 h-3.5" />
            {uploadingCardPhoto ? "Enviando…" : "Trocar foto"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploadingCardPhoto}
              onChange={onQuickPhotoChange}
              data-testid="quick-change-photo-input"
            />
          </label>
        </div>
        <div className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Veículo</div>
              <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="vehicle-title">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              <button
                className="mt-1 text-sm text-primary hover:underline"
                onClick={() => navigate(`/clientes/${client?.id}`)}
                data-testid="client-link"
              >
                {client?.name || "—"}
              </button>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3">
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">VIN</div>
                <div className="font-mono text-sm">{vehicle.vin || "—"}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)} data-testid="transfer-client-btn">
                  <UserSwap className="w-3.5 h-3.5 mr-1.5" /> Transferir Cliente
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditVehicleOpen(true)} data-testid="edit-vehicle-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelVehicle(true)} className="text-red-500 hover:text-red-600 hover:bg-red-500/10" data-testid="delete-vehicle-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[
              ["Placa", vehicle.plate],
              ["Motor", vehicle.engine],
              ["Combustível", vehicle.fuel],
              ["Mileage", vehicle.mileage ? `${vehicle.mileage.toLocaleString("en-US")} mi` : null],
              ["Versão", vehicle.trim],
              ["Tração", vehicle.drivetrain],
              ["Total de OS", stats.total],
              ["OS Ativas", stats.ativos],
            ].map(([k, v]) => (
              <div key={k} className="border border-border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                <div className="font-mono mt-1 text-sm">{v || (v === 0 ? "0" : "—")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-muted-foreground" />
            Histórico de Ordens de Serviço
          </h2>
          <Button variant="outline" size="sm" onClick={() => navigate(`/check-in?vehicle_id=${vehicle.id}`)} data-testid="new-job-for-vehicle-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nova OS
          </Button>
        </div>
        {jobs.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
            Nenhuma OS registrada para este veículo.
          </div>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border overflow-hidden">
            {jobs.map((j) => (
              <div key={j.id} className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 hover:bg-accent/40 transition" data-testid={`job-row-${j.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{j.service_type || "OS"}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${priorityBadge(j.priority)}`}>{j.priority || "normal"}</span>
                    <span className="text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">{STATUS_LABELS[j.status] || j.status}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span className="font-mono">#{j.id.slice(-6).toUpperCase()}</span>
                    <span>Técnico: {j.technician || "—"}</span>
                    <span className="font-mono">{new Date(j.created_at).toLocaleDateString("pt-BR")}</span>
                  </div>
                  {j.notes && <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{j.notes}</div>}
                </div>
                <div className="flex gap-1 shrink-0 self-end md:self-auto">
                  <Button size="sm" variant="ghost" onClick={() => setEditJobId(j.id)} data-testid={`edit-job-${j.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelJob(j)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-job-${j.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/inspecoes?job_id=${j.id}`)} data-testid={`open-job-${j.id}`}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <EditarVeiculoDialog
        vehicle={vehicle}
        open={editVehicleOpen}
        onOpenChange={setEditVehicleOpen}
        onSaved={load}
      />
      <TransferirClienteDialog
        vehicle={vehicle}
        currentClient={client}
        open={transferOpen}
        onOpenChange={setTransferOpen}
        onTransferred={load}
      />
      <EditarVeiculoClienteDialog
        jobId={editJobId}
        open={!!editJobId}
        onOpenChange={(v) => !v && setEditJobId(null)}
        onSaved={load}
      />

      {/* Confirm Delete Vehicle */}
      <Dialog open={confirmDelVehicle} onOpenChange={setConfirmDelVehicle}>
        <DialogContent data-testid="confirm-delete-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Excluir Veículo</DialogTitle>
            <DialogDescription>
              Isso removerá o veículo permanentemente. As OS vinculadas não serão excluídas automaticamente.
              Digite <b>EXCLUIR</b> para confirmar.
            </DialogDescription>
          </DialogHeader>
          <ConfirmDeleteBox onConfirm={deleteVehicle} onCancel={() => setConfirmDelVehicle(false)} />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Job */}
      <Dialog open={!!confirmDelJob} onOpenChange={(v) => !v && setConfirmDelJob(null)}>
        <DialogContent data-testid="confirm-delete-job-dialog">
          <DialogHeader>
            <DialogTitle>Excluir Ordem de Serviço</DialogTitle>
            <DialogDescription>
              A OS <b>#{confirmDelJob?.id.slice(-6).toUpperCase()}</b> (<i>{confirmDelJob?.service_type}</i>) será removida.
              Inspeções vinculadas continuarão existindo, mas o vínculo se perderá.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelJob(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => deleteJob(confirmDelJob.id)} data-testid="confirm-delete-job-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" /> Excluir OS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfirmDeleteBox({ onConfirm, onCancel }) {
  const [txt, setTxt] = useState("");
  return (
    <div className="space-y-3">
      <Input
        placeholder="Digite EXCLUIR"
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        autoFocus
        data-testid="delete-confirm-input"
      />
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button
          variant="destructive"
          disabled={txt.trim() !== "EXCLUIR"}
          onClick={onConfirm}
          data-testid="delete-confirm-btn"
        >
          <Trash2 className="w-4 h-4 mr-1.5" /> Excluir
        </Button>
      </DialogFooter>
    </div>
  );
}
